import { Typography } from "@mui/material";
import type {
  DataGridPremiumProps,
  GridColDef,
  GridGroupingColDefOverride,
  GridInitialState,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowParams,
  GridRowSelectionModel,
  GridSortDirection,
} from "@mui/x-data-grid-premium";
import { GRID_TREE_DATA_GROUPING_FIELD, GridCellCheckboxRenderer, useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import {
  getRowSelectionModelFromIds,
  getSelectedRowIdsFromModel,
} from "@entrilia/shared/components/grid/gridHelper.ts";
import { getRowNode } from "@entrilia/shared/utilities/dataGridApiHelper.ts";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { ReportInfo } from "../../../../../../api/types/svTypes.ts";
import GroupHeaderWithSelection from "../../../../../common/grid/GroupHeaderWithSelection";
import { useReportsManagerContext } from "../../contexts/reports-manager/ReportsManagerContext";
import ReportMenuAction from "../components/ReportMenuAction";
import useGridExpand from "../hooks/useGridExpand";
import type { ReportInfoExt } from "../types/reportTypes";
import { getViewApiReportUrl, getViewSystemReportUrl, getViewUiReportUrl } from "../utilities/editReportUrl";
import { CustomToolbarActions } from "./CustomToolbarActions";
import { getGroupedNameSortComparator } from "./groupedNameSortComparator";
import GroupName from "./GroupName";
import GroupSharingCell from "./GroupSharingCell";
import NameColumn from "./NameColumn";
import { getReportsGridSx } from "./reportsGridSx";
import SharingCell from "./SharingCell";

interface Props {
  reports: ReportInfoExt[];
  isGroupSelected?: boolean;
  onToggleFavorite?: (reportId: string) => void;
}

const getRowId = (row: ReportInfoExt) => row.reportId;

const isRowSelectable = ({ row }: GridRowParams<ReportInfoExt>): boolean => !!row.reportId;

const initialGridState: GridInitialState = {
  sorting: { sortModel: [{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: "asc" }] },
};

const sortingOrder: GridSortDirection[] = ["asc", "desc"];

export default function ReportsGrid({ reports, isGroupSelected, onToggleFavorite }: Props) {
  const { copyReports, deleteReports, moveReports, shareReport, shareReports } = useReportsManagerContext();
  const [currentReport, setCurrentReport] = useState<ReportInfoExt>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const apiRef = useGridApiRef();
  const { isGroupExpandedByDefault, toggleSingleGridRow, toggleAllGridRows } = useGridExpand({
    gridKey: "COMPANY_UI_REPORTS_GRID",
    apiRef,
  });

  const sortedReports = useMemo(() => [...reports].sort((a, b) => a.groupOrder - b.groupOrder), [reports]);

  const treeDataPath = useMemo<DataGridPremiumProps["getTreeDataPath"]>(
    () => (row: ReportInfoExt) => (isGroupSelected ? [row.reportId] : [row.group, row.reportId]),
    [isGroupSelected]
  );
  const showToolbar = useMemo(() => selectedIds.length > 0, [selectedIds]);
  const selectedReports = useMemo(
    () => selectedIds.map((id) => reports.find((r) => r.reportId === id)).filter((r): r is ReportInfoExt => !!r),
    [reports, selectedIds]
  );
  const fullSelection = useMemo(
    () => reports.length > 0 && selectedReports.length === reports.length,
    [reports.length, selectedReports.length]
  );

  const groupColumnDef = useMemo(
    (): GridGroupingColDefOverride => ({
      hideDescendantCount: true,
      headerName: "Name",
      sortable: true,
      aggregable: false,
      hideable: false,
      pinnable: false,
      filterable: false,
      flex: 1,
      getSortComparator: getGroupedNameSortComparator(apiRef),
      renderHeader: (params) => (
        <GroupHeaderWithSelection
          headerName={params.colDef.headerName}
          permissions={["ManageReports"]}
          hideExpandCollapse={isGroupSelected}
          onExpandToggle={toggleAllGridRows}
        />
      ),
      renderCell: (params: GridRenderCellParams<ReportInfoExt, unknown, unknown>) => {
        if (params.rowNode.type === "group") {
          return <GroupName params={params} permissions={["ManageReports"]} onExpandToggle={toggleSingleGridRow} />;
        }
        return (
          <NameColumn
            report={params.row}
            href={getReportUrl(params.row)}
            permissions={["ManageReports"]}
            reportType={params.row.reportType}
            isFavorite={params.row.isFavorite}
            isGroupSelected={isGroupSelected}
            onToggleFavorite={onToggleFavorite ? () => onToggleFavorite(params.row.reportId) : undefined}
          >
            <GridCellCheckboxRenderer {...params} />
          </NameColumn>
        );
      },
    }),
    [apiRef, isGroupSelected, toggleAllGridRows, onToggleFavorite, toggleSingleGridRow]
  );

  const columns = useMemo(
    (): GridColDef<ReportInfoExt>[] => [
      {
        field: nameof<ReportInfoExt>("authorization"),
        headerName: "Sharing",
        flex: 0.3,
        valueGetter: (_, row) => row.authorization?.private ?? false,
        renderCell: (params) => {
          if (params.rowNode.type === "group") {
            return <GroupSharingCell caption={String(params.rowNode.groupingKey ?? "")} />;
          }
          return <SharingCell report={params.row} onShareClick={shareReport} />;
        },
      },
      {
        field: nameof<ReportInfoExt>("createdBy"),
        headerName: "Created by",
        flex: 0.5,
      },
      {
        field: nameof<ReportInfoExt>("modifiedBy"),
        headerName: "Modified by",
        flex: 0.5,
      },
      {
        field: nameof<ReportInfoExt>("updateAt"),
        headerName: "Modified at",
        flex: 0.4,
        valueFormatter: (value) => convertISODateTime(value),
        renderCell: (params) => (
          <Typography className="MuiDataGrid-cellContent" color={"secondary"}>
            {params.formattedValue}
          </Typography>
        ),
      },
      {
        field: "actions",
        sortable: false,
        resizable: false,
        headerName: "Actions",
        width: 80,
        cellClassName: "grid-row-actions",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          if (params.rowNode.type === "group") {
            return null;
          }
          return <ReportMenuAction report={params.row} onReportSelected={setCurrentReport} />;
        },
      },
    ],
    [shareReport]
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<ReportInfoExt>) => {
      if (getRowNode(apiRef, params.id)?.type === "group") {
        return "";
      }
      if (params.row.reportId === currentReport?.reportId) {
        return "Mui-hovered";
      }
      return "";
    },
    [apiRef, currentReport]
  );

  const handleRowSelectionChanged = useCallback(
    (ids: GridRowSelectionModel) => {
      setSelectedIds(getSelectedRowIdsFromModel(ids));
    },
    [setSelectedIds]
  );

  const handleMoveReports = useCallback((ids: string[]) => moveReports(ids, () => setSelectedIds([])), [moveReports]);

  const handleShareReports = useCallback(
    (ids: string[]) => shareReports(ids, () => setSelectedIds([])),
    [shareReports]
  );

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      style={{ paddingLeft: 0 }}
      fullSelection={fullSelection}
      selection={[selectedIds, setSelectedIds]}
      actions={
        <CustomToolbarActions
          selectedReports={selectedReports}
          onCopy={copyReports}
          onDelete={deleteReports}
          onMove={handleMoveReports}
          onUpdateSharing={handleShareReports}
        />
      }
    >
      <DataGrid<ReportInfoExt>
        apiRef={apiRef}
        treeData
        getTreeDataPath={treeDataPath}
        columns={columns}
        rows={sortedReports}
        getRowId={getRowId}
        isRowSelectable={isRowSelectable}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        sx={getReportsGridSx}
        groupingColDef={groupColumnDef}
        defaultGroupingExpansionDepth={isGroupSelected ? 0 : 1}
        isGroupExpandedByDefault={isGroupSelected ? undefined : isGroupExpandedByDefault}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={false}
        disableAggregation
        getRowClassName={getRowClassName}
        rowSelectionModel={getRowSelectionModelFromIds(selectedIds)}
        sortingOrder={sortingOrder}
        disableMultipleColumnsSorting
        initialState={initialGridState}
        onRowSelectionModelChange={handleRowSelectionChanged}
        shiftActionsLeft
      />
    </DataGridHeaderToolbarProvider>
  );
}

const getReportUrl = (reportInfo: ReportInfo) => {
  switch (reportInfo.reportUsage) {
    case "Api":
      return getViewApiReportUrl(reportInfo.clientCode, reportInfo.reportId);
    case "Ui":
      return getViewUiReportUrl(reportInfo.clientCode, reportInfo.reportId);
    case "System":
      return getViewSystemReportUrl(reportInfo.clientCode, reportInfo.reportId);
    default:
      throw new Error(`Unsupported report usage: ${reportInfo.reportUsage}`);
  }
};
